import React from 'react'
import { Breadcrumb, Button, ActionMenu, StatusBadge } from '../../../components/ui'
import Icon from '../../../components/Icon'
import { getBudgetExpenseMarker } from '../budgetExpenseVisuals'
import './DetailPageScaffold.css'
import { useBack, EntityLink } from '../../../navigation/navigation'

export function statusVariant(status) {
  const normalized = String(status || '').toLowerCase().trim()
  if (normalized === 'в работе' || normalized === 'active' || normalized === 'действующий') return 'active'
  if (normalized === 'завершен' || normalized === 'завершён' || normalized === 'работы завершены' || normalized === 'согласован') return 'success'
  if (normalized === 'в согласовании' || normalized === 'в рассмотрении' || normalized === 'на согласовании') return 'review'
  if (normalized === 'приостановлен' || normalized === 'отменен' || normalized === 'отменён' || normalized === 'просрочена') return 'warning'
  return 'draft'
}

export function DetailPageHeader({ breadcrumbs, title, status, subtitle, onBack, actions = [] }) {
  const back = useBack(onBack)
  return (
    <header className="entity-screen-header">
      <Breadcrumb items={breadcrumbs} className="entity-screen-breadcrumbs" />
      <div className="entity-screen-heading">
        <div className="entity-screen-heading__main">
          <div className="entity-screen-title-row">
            <Button type="button" variant="ghost" className="entity-screen-back" onClick={back} aria-label="Назад">
              <Icon name="chevronLeft" size={18} />
            </Button>
            <h1 className="ds-page-title">{title}</h1>
            {status ? <StatusBadge variant={statusVariant(status)}>{status}</StatusBadge> : null}
          </div>
          {subtitle ? <div className="entity-screen-subtitle">{subtitle}</div> : null}
        </div>
        <ActionMenu label="Действия" variant="primary" items={actions} className="entity-screen-actions" />
      </div>
    </header>
  )
}

export function DetailSection({ title, actions, children, className = '' }) {
  return (
    <section className={`entity-detail-section ds-card ${className}`.trim()}>
      {(title || actions) ? (
        <div className="entity-detail-section__header">
          {title ? <h2 className="ds-section-heading">{title}</h2> : <span />}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  )
}

export function InfoGrid({ items }) {
  const visibleItems = (items || []).filter(Boolean)
  if (!visibleItems.length) return null
  return (
    <div className="entity-info-grid">
      {visibleItems.map((item) => (
        <div key={item.key || item.label} className={item.full ? 'entity-info-grid__item entity-info-grid__item--full' : 'entity-info-grid__item'}>
          <div className="ds-info-label">{item.label}</div>
          <div className={`ds-info-value ${item.muted ? 'entity-info-grid__muted' : ''}`.trim()}>{item.value}</div>
        </div>
      ))}
    </div>
  )
}

export function KpiStrip({ items }) {
  const visibleItems = (items || []).filter(Boolean)
  if (!visibleItems.length) return null
  return (
    <div className="entity-kpi-strip">
      {visibleItems.map((item) => (
        <div key={item.key || item.label} className={`entity-kpi-card entity-kpi-card--${item.tone || 'default'}`}>
          <div className="entity-kpi-label">{item.label}</div>
          <div className="entity-kpi-value">{item.value}</div>
          {item.meta ? <div className="entity-kpi-meta">{item.meta}</div> : null}
        </div>
      ))}
    </div>
  )
}

export function ProgressMini({ percent = 0, tone = 'grey' }) {
  const safePercent = Math.max(0, Math.min(Number(percent) || 0, 100))
  return (
    <div className="entity-progress-mini" aria-hidden>
      <div className={`entity-progress-mini__fill entity-progress-mini__fill--${tone}`} style={{ width: `${safePercent}%` }} />
    </div>
  )
}

export function SummaryCard({ title, status, meta, amount, sub, progress, progressTone, overdue, onClick, to, rightMeta }) {
  const Component = to ? EntityLink : 'button'
  return (
    <Component type={to ? undefined : 'button'} to={to} className={`entity-summary-card ${overdue ? 'entity-summary-card--overdue' : ''}`.trim()} onClick={onClick}>
      <div className="entity-summary-card__left">
        <div className="entity-summary-card__title">
          <span>{title}</span>
          {status ? <StatusBadge variant={statusVariant(status)}>{status}</StatusBadge> : null}
        </div>
        {meta ? <div className="entity-summary-card__meta">{meta}</div> : null}
        {progress != null ? <ProgressMini percent={progress} tone={progressTone} /> : null}
      </div>
      <div className="entity-summary-card__right">
        {amount ? <div className="entity-summary-card__amount">{amount}</div> : null}
        {sub ? <div className="entity-summary-card__sub">{sub}</div> : null}
        {rightMeta ? <div className="entity-summary-card__sub">{rightMeta}</div> : null}
      </div>
      <span className="entity-summary-card__arrow" aria-hidden>›</span>
    </Component>
  )
}

export function BudgetMatrix({ columns, rows, totalLabel = 'Итого', emptyText = 'Данные бюджета не найдены', ariaLabel }) {
  const visibleColumns = columns || []
  const visibleRows = rows || []
  if (!visibleColumns.length || !visibleRows.length) {
    return <p className="info-note">{emptyText}</p>
  }

  const totals = visibleColumns.map((_, columnIdx) => visibleRows.reduce((sum, row) => sum + Number(row.values?.[columnIdx] || 0), 0))
  const grandTotal = totals.reduce((sum, value) => sum + value, 0)

  return (
    <div className="entity-budget-table-wrap">
      <table className="entity-budget-table" aria-label={ariaLabel}>
        <thead>
          <tr>
            <th>Подразделение</th>
            {visibleColumns.map((column) => (
              <th key={column.key || column.label} className="align-right">
                <div className="entity-budget-table__stage-heading">
                  <span className="entity-budget-table__stage-type">{column.label}</span>
                  {column.subLabel ? (
                    <button
                      type="button"
                      className="entity-budget-table__stage-link"
                      onClick={column.onSubLabelClick}
                      disabled={!column.onSubLabelClick}
                    >
                      {column.subLabel}
                    </button>
                  ) : null}
                </div>
              </th>
            ))}
            <th className="align-right entity-budget-table__total">Итого по объекту</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, rowIdx) => {
            const previousSection = visibleRows[rowIdx - 1]?.section
            const showSection = row.section && row.section !== previousSection
            return (
              <React.Fragment key={row.key || row.label}>
                {showSection ? (
                  <tr className="entity-budget-table__section">
                    <td colSpan={visibleColumns.length + 2}>{row.section}</td>
                  </tr>
                ) : null}
                <tr className={[
                  row.expenseKind ? 'entity-budget-table__expense' : '',
                  row.expenseKind ? `entity-budget-table__expense--${row.expenseKind}` : '',
                ].filter(Boolean).join(' ')}>
                  <td className="entity-budget-table__dept">
                    {row.expenseKind ? (
                      <span className={`entity-budget-table__expense-marker entity-budget-table__expense-marker--${row.expenseKind}`} aria-hidden="true">
                        {getBudgetExpenseMarker(row.expenseKind)}
                      </span>
                    ) : null}
                    <span>{row.label}</span>
                  </td>
                  {visibleColumns.map((column, columnIdx) => (
                    <td key={`${row.key || row.label}-${column.key || column.label}`} className={`amount-mono ${Number(row.values?.[columnIdx] || 0) === 0 ? 'zero' : ''}`.trim()}>
                      {row.formatValue(row.values?.[columnIdx] || 0)}
                    </td>
                  ))}
                  <td className="amount-mono entity-budget-table__total">{row.formatValue(row.total)}</td>
                </tr>
              </React.Fragment>
            )
          })}
          <tr className="entity-budget-table__summary">
            <td>{totalLabel}</td>
            {totals.map((total, idx) => (
              <td key={visibleColumns[idx].key || visibleColumns[idx].label} className="amount-mono">
                {visibleRows[0].formatValue(total)}
              </td>
            ))}
            <td className="amount-mono entity-budget-table__total">{visibleRows[0].formatValue(grandTotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export function BudgetLinkCard({ title, sub, amount, pct, tone = 'green', onClick }) {
  return (
    <button type="button" className="entity-budget-link-card" onClick={onClick}>
      <div className="entity-budget-link-card__left">
        <div className={`entity-budget-link-card__icon entity-budget-link-card__icon--${tone}`}>
          <Icon name="chart" size={18} />
        </div>
        <div>
          <div className="entity-budget-link-card__title">{title}</div>
          {sub ? <div className="entity-budget-link-card__sub">{sub}</div> : null}
        </div>
      </div>
      <div className="entity-budget-link-card__right">
        <div className="entity-budget-link-card__amount">{amount}</div>
        <div className="entity-budget-link-card__pct">{pct}</div>
      </div>
      <span className="entity-budget-link-card__arrow" aria-hidden>›</span>
    </button>
  )
}
